import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const categories = [
    { id: 'all', label: 'All Questions' },
    { id: 'bungee', label: 'Bungee Fitness' },
    { id: 'aerial', label: 'Aerial Kids' },
    { id: 'coaching', label: 'Personal Operations' },
    { id: 'general', label: 'General' }
  ];

  const categoryTitles = {
    bungee: 'Bungee Fitness: Defying Gravity',
    aerial: 'Aerial Kids: Air Time > Screen Time',
    coaching: 'Personal Operations',
    general: 'General: The Basics'
  };

  useEffect(() => {
    fetchFAQs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/public/faqs`);
      if (response.ok) {
        const data = await response.json();
        setFaqs(data.faqs || []);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const filteredFaqs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const groupedFaqs = filteredFaqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  // Generate FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="faq-page">
      {/* SEO FAQ Schema */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <div className="faq-hero">
        <h1>Frequently Asked (and Unasked) Questions</h1>
        <p>Everything you need to know before we get to work.</p>
      </div>

      <div className="faq-content">
        {/* Category Filter */}
        <div className="faq-categories">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`faq-category-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`faq-category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="faq-loading">Loading...</div>
        ) : filteredFaqs.length === 0 ? (
          <div className="faq-empty">
            <p>No FAQs found. Check back soon!</p>
          </div>
        ) : (
          <div className="faq-sections">
            {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
              <section key={category} id={`faq-${category}`} className="faq-section">
                <h2 className="faq-section-title">{categoryTitles[category] || category}</h2>
                <div className="faq-accordion">
                  {categoryFaqs.map((faq) => (
                    <div 
                      key={faq.id} 
                      className={`faq-item ${expandedIds.has(faq.id) ? 'expanded' : ''}`}
                    >
                      <button 
                        className="faq-question"
                        onClick={() => toggleExpand(faq.id)}
                        data-testid={`faq-question-${faq.id}`}
                      >
                        <span>{faq.question}</span>
                        {expandedIds.has(faq.id) ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </button>
                      <div className="faq-answer">
                        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(faq.answer) }} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="faq-cta">
          <h3>Still have questions?</h3>
          <p>We're here to help. Reach out and let's chat.</p>
          <button 
            className="faq-cta-btn" 
            data-testid="faq-contact-btn"
            onClick={() => {
              navigate('/consulting');
              setTimeout(() => {
                document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
          >
            Get in Touch
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
